import { BillingAccountKind } from "@unacast-internal/unacat-ts/gen/unacast/subscription/v1/billing_account_pb"

declare global {
  interface Window {
    // Set by the root route from the server's POSTHOG_ENV (see root.tsx), so
    // one build serves both production and previews but only production
    // sends browser events. Missing means not production.
    __POSTHOG_ENABLED__?: boolean
  }
}

let initialized = false
let warnedDisabled = false
let listenersAttached = false
let posthog: any
let initInFlight: Promise<void> | null = null
const pendingCallbacks: Array<() => void> = []

function normalizeAccountKind(
  value: BillingAccountKind | string | null | undefined,
): string | undefined {
  if (value == null) return undefined
  if (typeof value === "string") return value
  try {
    const key = (BillingAccountKind as unknown as Record<number, string>)[
      value as number
    ]
    return key || String(value)
  } catch (_e) {
    return String(value)
  }
}

function attachGlobalErrorListeners(): void {
  if (typeof window === "undefined" || listenersAttached) return
  window.addEventListener("error", (event: ErrorEvent) => {
    captureClientError(event.error ?? event.message, {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    captureClientError(event.reason, { source: "unhandledrejection" })
  })
  listenersAttached = true
}

export function initPosthog(): void {
  if (typeof window === "undefined" || initialized || initInFlight) return
  const enabled = import.meta.env.VITE_POSTHOG_ENABLED === "true"
  if (!enabled) return
  if (!window.__POSTHOG_ENABLED__) {
    if (!warnedDisabled) {
      warnedDisabled = true
      console.info("[PostHog] Not a production deployment; browser events are not sent.")
    }
    return
  }
  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  const apiHost = import.meta.env.VITE_POSTHOG_PROXY_PATH as string | undefined
  const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST as string | undefined
  if (!apiKey) {
    console.warn("VITE_POSTHOG_KEY not set; PostHog will not be initialized.")
    return
  }
  if (!apiHost || !uiHost) return

  initInFlight = import("posthog-js")
    .then(({ default: ph }) => {
      posthog = ph
      ph.init(apiKey, {
        api_host: apiHost,
        ui_host: uiHost,
        // "history_change" also captures $pageview on client-side (pushState)
        // navigations. `true` only fires on the initial full page load, which
        // in a React Router SPA meant only entry pages showed up (CAT-1181).
        capture_pageview: "history_change",
        capture_performance: true,
        session_recording: {
          maskAllInputs: false,
          maskInputFn: (text, element) => {
            if (element?.getAttribute?.("type") === "password") {
              return "•".repeat(text.length)
            }
            return text
          },
        },
      })
      attachGlobalErrorListeners()
      initialized = true
      // flush queued callbacks
      while (pendingCallbacks.length) {
        const cb = pendingCallbacks.shift()
        try {
          cb && cb()
        } catch (_e) {
          // ignore listener errors
        }
      }
    })
    .catch(() => {
      // ignore load errors
    })
    .finally(() => {
      initInFlight = null
    })
}

// Removed legacy identifyUserIfAvailable – use identifyWithContext instead.

/**
 * Identify the current user with richer context and group information.
 * Safe to call repeatedly; it will no-op if PostHog isn't ready.
 */
export function identifyWithContext(
  distinctId: string,
  userProps?: { email?: string | null; displayName?: string | null },
  context?: {
    accountId?: string | null
    accountName?: string | null
    accountKind?: BillingAccountKind | string | null
    orgScopeId?: string | null
    orgScopeName?: string | null
  },
): void {
  if (typeof window === "undefined") return
  try {
    if (!initialized) initPosthog()

    const personProps: Record<string, unknown> = {
      email: userProps?.email || undefined,
      displayName: userProps?.displayName || undefined,
    }

    const run = () => {
      if (!posthog) return
      posthog.identify(distinctId, personProps)

      const accountId = context?.accountId || undefined
      const accountProps: Record<string, unknown> = {
        account_id: accountId,
        account_name: context?.accountName || undefined,
        account_kind: normalizeAccountKind(context?.accountKind),
        org_scope_id: context?.orgScopeId || undefined,
        org_scope_name: context?.orgScopeName || undefined,
      }

      posthog.register(accountProps)

      if (typeof posthog.group === "function" && accountId) {
        try {
          posthog.group("account", accountId, {
            name: context?.accountName || undefined,
            kind: normalizeAccountKind(context?.accountKind),
          })
        } catch (_e) {}
      }

      if (typeof posthog.group === "function" && context?.orgScopeId) {
        try {
          posthog.group("org_scope", context.orgScopeId, {
            name: context?.orgScopeName || undefined,
            account_id: accountId,
          })
        } catch (_e) {}
      }
    }

    if (posthog) run()
    else if (initInFlight) pendingCallbacks.push(run)
    else pendingCallbacks.push(run)
  } catch (_err) {
    // best-effort only
  }
}

/**
 * Reset PostHog identity (e.g., on logout) to avoid cross-user leakage.
 */
export function resetPosthogIdentity(): void {
  if (typeof window === "undefined") return
  try {
    if (!initialized) initPosthog()

    const run = () => {
      if (!posthog) return
      try {
        posthog.unregister("account_id")
        posthog.unregister("account_name")
        posthog.unregister("account_kind")
        posthog.unregister("org_scope_id")
        posthog.unregister("org_scope_name")
      } catch (_e) {}
      posthog.reset()
    }

    if (posthog) run()
    else if (initInFlight) pendingCallbacks.push(run)
    else pendingCallbacks.push(run)
  } catch (_e) {
    // best-effort only
  }
}

export function captureErrorPage(
  errorType: "no_access" | "not_found" | "rate_limited" | "unexpected",
  statusCode?: number,
): void {
  try {
    if (!initialized) initPosthog()

    const run = () => {
      if (!posthog) return
      posthog.capture("user_hit_error_page", {
        error_type: errorType,
        status_code: statusCode,
      })
    }

    if (posthog) run()
    else if (initInFlight) pendingCallbacks.push(run)
    else pendingCallbacks.push(run)
  } catch (_e) {
    // best-effort only
  }
}

export function captureClientError(error: unknown, properties?: Record<string, unknown>): void {
  try {
    if (!initialized) initPosthog()
    if (!posthog) return
    if (error instanceof Error) {
      posthog.capture("$exception", {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace: error.stack,
        ...properties,
      })
    } else {
      posthog.capture("client_error", {
        error: String(error),
        ...properties,
      })
    }
  } catch (_e) {
    // best-effort only
  }
}

/**
 * Capture a PostHog event. Safe to call before PostHog is ready; events are queued.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return
  try {
    if (!initialized) initPosthog()

    const run = () => {
      if (!posthog) return
      posthog.capture(event, properties)
    }

    if (posthog) run()
    else pendingCallbacks.push(run)
  } catch (_e) {
    // best-effort only
  }
}

export { posthog }


